/**
 * MentionTextArea
 *
 * Reusable @-mention textarea for EDIT surfaces (inline description, IO note, comment edit). Sources
 * the org's task-management users from TaskProvider (the same list the create surfaces use) and
 * renders the Facebook-style @ dropdown. Controlled via value/onChange so it works standalone or as
 * a Form.Item child. Caps length and remounts once after first paint to fix antd's autoSize
 * first-mount measurement (see reference_antd_mentions_autosize_gotchas). Dropdown stays on the
 * default <body> portal — do NOT add getPopupContainer (it clips/hides the dropdown).
 */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Mentions } from 'antd';
import { useTaskContext } from '../../../../../core/components/tasks/useTaskContext';

const MentionTextArea = React.forwardRef(
  ({ value, onChange, maxLength, autoSize, placeholder, className, onKeyDown }, ref) => {
    const { mentionUsers } = useTaskContext();
    const [search, setSearch] = useState('');
    const [measureKey, setMeasureKey] = useState(0);

    useEffect(() => {
      const raf = requestAnimationFrame(() => setMeasureKey((k) => k + 1));
      return () => cancelAnimationFrame(raf);
    }, []);

    const options = (mentionUsers || [])
      .filter((u) => {
        if (!u.username) return false;
        if (!search) return true;
        const q = search.toLowerCase();
        return (u.name && u.name.toLowerCase().includes(q)) || u.username.toLowerCase().includes(q);
      })
      .slice(0, 20)
      .map((u) => (
        <Mentions.Option key={u.id} value={u.username}>
          {u.name ? `${u.name} (@${u.username})` : `@${u.username}`}
        </Mentions.Option>
      ));

    return (
      <Mentions
        key={measureKey}
        ref={ref}
        value={value}
        onChange={(text) => onChange(maxLength ? text.slice(0, maxLength) : text)}
        onSearch={(t) => setSearch(t)}
        onKeyDown={onKeyDown}
        filterOption={false}
        autoSize={autoSize}
        maxLength={maxLength}
        placeholder={placeholder}
        className={className}
      >
        {options}
      </Mentions>
    );
  }
);

MentionTextArea.displayName = 'MentionTextArea';

MentionTextArea.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  maxLength: PropTypes.number,
  autoSize: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  placeholder: PropTypes.string,
  className: PropTypes.string,
  onKeyDown: PropTypes.func,
};

MentionTextArea.defaultProps = {
  value: undefined,
  // No length cap by default — callers that want one (e.g. comments) pass maxLength explicitly.
  // Defaulting to a cap would silently truncate previously-uncapped fields (e.g. the IO Note).
  maxLength: undefined,
  autoSize: { minRows: 2, maxRows: 8 },
  placeholder: '',
  className: 'custom-textarea',
  onKeyDown: undefined,
};

export default MentionTextArea;
